import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { ContractReadyNotice } from '~/features/market/contracts/ContractReadyNotice';
import {
  headlineValue,
  JOB_TERM_GROUPS,
  providerJobToVersions,
} from '~/features/market/negotiations/negotiationHistory';
import type { MarketFact } from '~/features/shared/market-content';
import {
  MarketActionRow,
  MarketContentPanel,
  MarketFactGrid,
  MarketStatusPill,
} from '~/features/shared/market-content';
import {
  NegotiationHistory,
  negotiationParty,
} from '~/features/shared/market-content/NegotiationHistory';
import { JobOpportunity } from '../types';
import { formatDate, formatHourlyRate, formatWorkLocation } from '../utils';

type Props = {
  opportunity: JobOpportunity;
  onAccept: () => void;
  onCounter: (options?: { startFirstNegotiation: boolean }) => void;
  onWithdraw: () => void;
  actionsDisabled?: boolean;
  identityControls?: ReactNode;
};

type TurnStatus = Parameters<typeof MarketStatusPill>[0]['status'];
type TurnDisplay = { label: string; status: TurnStatus; pulse: boolean } | null;

const getTurnDisplay = (turn: JobOpportunity['turn']): TurnDisplay => {
  if (turn === 'YOUR_TURN') {
    return { label: 'Your Turn', status: 'yourTurn', pulse: true };
  }
  if (turn === 'THEIR_TURN') {
    return { label: 'Their Turn', status: 'theirTurn', pulse: false };
  }
  return null;
};

export const OpportunityDetail = ({
  opportunity,
  onAccept,
  onCounter,
  onWithdraw,
  actionsDisabled,
  identityControls,
}: Props) => {
  const { listing, listingOwner, latestNegotiation } = opportunity;
  const turnDisplay = getTurnDisplay(opportunity.turn);
  const versions = useMemo(
    () => providerJobToVersions(opportunity.negotiations),
    [opportunity.negotiations]
  );

  const showAccept = opportunity.availableActions.includes('accept');
  const showCounter = opportunity.availableActions.includes('counter');
  const showWithdraw = opportunity.availableActions.includes('withdraw');
  const showActions = showAccept || showCounter || showWithdraw;
  const counterLabel = latestNegotiation ? 'Counter' : 'Negotiate';
  const facts = (
    [
      { label: 'Job Type', value: listing.jobType },
      listing.specialtyName ? { label: 'Specialty', value: listing.specialtyName } : null,
      { label: 'States', value: listing.stateCodes.join(', ') || '—' },
      { label: 'Work Location', value: formatWorkLocation(listing.workLocation) },
      listing.pricePerHour != null
        ? {
            label: 'Compensation',
            value: formatHourlyRate(listing.pricePerHour) ?? '',
            highlight: true,
          }
        : null,
      listing.startDate || listing.endDate
        ? {
            label: 'Duration',
            value:
              listing.startDate && listing.endDate
                ? `${formatDate(listing.startDate)} – ${formatDate(listing.endDate)}`
                : listing.startDate
                  ? `${formatDate(listing.startDate)}+`
                  : `Until ${formatDate(listing.endDate)}`,
          }
        : null,
      listing.numOfShifts != null
        ? { label: 'Total Shifts', value: String(listing.numOfShifts) }
        : null,
      listing.hoursPerShift != null ? { label: 'Hours/Shift', value: listing.hoursPerShift } : null,
    ] as Array<MarketFact | null>
  ).filter((fact): fact is MarketFact => fact !== null);

  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      dataCy="job-opportunity-detail"
      variant="scroll"
    >
      {/* Listing Summary */}
      <div className="shrink-0 border-b border-grey-900 p-4 min-[1536px]:p-5">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <MarketStatusPill status="responded" label="JOB" />
            <h2 className="sub-heading-sb text-content-primary">{listing.jobTitle}</h2>
          </div>
          {turnDisplay && (
            <MarketStatusPill
              status={turnDisplay.status}
              label={turnDisplay.label}
              pulse={turnDisplay.pulse}
            />
          )}
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 text-[13px] text-content-secondary">
          <span>Listed by</span>
          {listingOwner.masked ? (
            <span className="text-xs font-semibold text-grey-500">ID</span>
          ) : (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-primary-t-50 text-[10px] font-bold text-white">
              {listingOwner.displayName.charAt(0)}
            </span>
          )}
          <span className="font-medium text-content-primary">{listingOwner.displayName}</span>
        </div>

        <MarketFactGrid facts={facts} density="compact" />
        {/* [MCV-1954] Both parties accepted: the contract is waiting on the Contracts page. */}
        {opportunity.status === 'ACCEPTED' && (
          <div className="mt-5">
            <ContractReadyNotice />
          </div>
        )}
        {identityControls && <div className="mt-5">{identityControls}</div>}
      </div>

      <div className="shrink-0 p-4 min-[1536px]:p-5">
        <NegotiationHistory
          key={opportunity.responseId}
          // The provider's page has always listed the rounds outright.
          defaultExpanded
          rounds={opportunity.negotiations.length}
          latest={
            latestNegotiation && {
              version: latestNegotiation.version,
              party: negotiationParty(latestNegotiation.sender, 'PROVIDER'),
              headline: {
                shifts:
                  latestNegotiation.numOfShifts != null
                    ? String(latestNegotiation.numOfShifts)
                    : null,
                rate: formatHourlyRate(latestNegotiation.pricePerHour) || null,
                value: headlineValue(latestNegotiation.totalContractValue),
              },
            }
          }
          viewerSender="PROVIDER"
          versions={versions}
          termGroups={JOB_TERM_GROUPS}
          emptyMessage="No negotiations yet"
        />

        {/* Action Buttons */}
        {showActions && (
          <MarketActionRow
            align="center"
            className="mt-3 shrink-0"
            actions={[
              ...(showAccept
                ? [
                    {
                      label: 'Accept',
                      cyTag: 'btn-accept',
                      onClick: onAccept,
                      disabled: actionsDisabled,
                      colorVariant: 'primary' as const,
                    },
                  ]
                : []),
              ...(showCounter
                ? [
                    {
                      label: counterLabel,
                      cyTag: 'btn-counter',
                      onClick: () =>
                        onCounter({
                          startFirstNegotiation: latestNegotiation == null,
                        }),
                      disabled: actionsDisabled,
                      colorVariant: 'secondary' as const,
                    },
                  ]
                : []),
              ...(showWithdraw
                ? [
                    {
                      label: 'Withdraw',
                      cyTag: 'btn-withdraw',
                      onClick: onWithdraw,
                      disabled: actionsDisabled,
                      colorVariant: 'tertiary' as const,
                    },
                  ]
                : []),
            ]}
          />
        )}
      </div>
    </MarketContentPanel>
  );
};
